import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Lock, Mic } from 'lucide-react';
import { apiService } from '../../services/api';
import DeleteMeetingButton from './DeleteMeetingButton';
import { useMeetingRecorder } from './MeetingRecorderContext';
import { formatDuration, formatMeetingDate, statusOf } from './meetingStatus';
import { ScopeChip } from './ScopePicker';

const POLL_MS = 15000;

// Réunions d'un projet : liste, état du traitement, accès à la synthèse.
const ProjectMeetings = ({ projectId }) => {
  const recorder = useMeetingRecorder();
  const [meetings, setMeetings] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => apiService.get(`/meetings?project_id=${projectId}`)
    .then((data) => { setMeetings(data.items); setError(null); })
    .catch((err) => setError(err.message)), [projectId]);

  useEffect(() => { load(); }, [load, recorder.phase]);

  // Tant qu'une réunion est en traitement, on rafraîchit.
  const busy = (meetings || []).some((m) => statusOf(m.status).busy);
  const own = (m) => (m.projects || []).find((p) => p.project_id === Number(projectId));
  useEffect(() => {
    if (!busy) return undefined;
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [busy, load]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Réunions</h2>
          <p className="text-sm text-slate-500">Enregistrées, transcrites et synthétisées par Nexus, sans sortir d'Innowide.</p>
        </div>
        <button type="button" onClick={() => recorder.openDialog({ projectId: Number(projectId) })}
          disabled={recorder.phase === 'recording' || recorder.phase === 'starting'}
          className="flex items-center gap-2 rounded-xl bg-rose-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rose-700 disabled:opacity-50">
          <Mic className="h-4 w-4" /> Enregistrer une réunion
        </button>
      </div>

      <div className="mt-3 divide-y divide-slate-100 rounded-xl border border-slate-100">
        {error && <p className="p-3 text-sm text-rose-600">{error}</p>}
        {meetings === null && !error && <p className="p-3 text-sm text-slate-400">Chargement…</p>}
        {meetings?.length === 0 && (
          <p className="p-4 text-sm text-slate-500">Aucune réunion enregistrée pour ce projet.</p>
        )}
        {meetings?.map((m) => {
          const status = statusOf(m.status);
          return (
            <div key={m.id} className="flex items-center gap-1 pr-2 hover:bg-slate-50">
            <Link to={`/meetings/${m.id}`} className="flex min-w-0 flex-1 items-start gap-3 p-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-slate-900">{m.title}</p>
                <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-slate-500">
                  {formatMeetingDate(m.started_at)}
                  {m.duration_seconds ? ` · ${formatDuration(m.duration_seconds)}` : ''}
                  {m.participants?.length ? ` · ${m.participants.map((p) => p.name).join(', ')}` : ''}
                  {m.scope && m.scope.kind !== 'project' && <ScopeChip meeting={m} />}
                </p>
                {/* Réunion d'un client ou d'un programme : ce qui revient à ce projet, sinon la synthèse. */}
                {(own(m)?.summary || m.summary_data?.overview) && (
                  <p className="mt-1 line-clamp-2 text-xs text-slate-600">{own(m)?.summary || m.summary_data.overview}</p>
                )}
              </div>
              {!m.can_read_transcript && <Lock className="mt-1 h-3.5 w-3.5 text-slate-300" title="Transcript réservé aux participants" />}
              <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${status.tone}`}>{status.label}</span>
            </Link>
            <DeleteMeetingButton meeting={m} onDeleted={(id) => setMeetings((prev) => prev.filter((x) => x.id !== id))} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProjectMeetings;
